import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    TabContent,
    DetailPanel,
    TabListWrapper,
    TabContentWrapper,
    ListDetailLayout,
    FileUploadRow,
    HiddenFileInput,
    UploadButton,
    SuccessMessage,
    ErrorMessage,
    EditActionsBar,
    FileLink,
} from '../../pages/admin/AdminPage.styles';
import useGetProcedureDetails from '../../api/useGetProcedureDetails';
import useGetProductsByProcedure from '../../api/useGetProductsByProcedure';
import useUpdateProcedure from '../../api/useUpdateProcedure';
import { useUploadFile } from '../../api/useUploadFile';
import { useDownloadFile } from '../../api/useDownloadFile';
import ProductsList from '../shared/ProductsList';
import { EditableInput, EditableTextarea } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import { getProcedureCategoryName } from '../../enums/ProcedureCategoryEnum';

interface ProcedureTabConfig {
    title: string;
    description: string;
    icon: string;
    category: number;
    columns: Array<{
        key: string;
        header: string;
        render?: (procedure: any) => React.ReactNode;
    }>;
    isEmergency?: boolean;
    detailSections?: Array<{
        title: string;
        content: (procedure: any) => React.ReactNode;
    }>;
}

interface ProcedureTabProps {
    config: ProcedureTabConfig;
    procedures: any[];
    loading: boolean;
    error: string | null;
    selectedProcedure: any;
    setSelectedProcedure: (procedure: any) => void;
    onRefresh?: () => void;
}

const ProcedureTab: React.FC<ProcedureTabProps> = ({ config, procedures, loading, error, selectedProcedure, setSelectedProcedure, onRefresh }) => {
    const { procedureDetails, loading: detailsLoading, error: detailsError, getProcedureDetails } = useGetProcedureDetails();
    const { products, loading: productsLoading, error: productsError, getProductsByProcedure } = useGetProductsByProcedure();
    const { updateProcedure, isLoading: isUpdating } = useUpdateProcedure();
    const { uploadFile, isUploading, uploadError, uploadSuccess, clearMessages } = useUploadFile();
    const { downloadFile, getFileUrl, isDownloading } = useDownloadFile();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const [selectedProcedureId, setSelectedProcedureId] = useState<number | null>(null);
    const [formData, setFormData] = useState<any>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const canEdit = loggedInUser !== null;

    // Initialize form data when procedure details are loaded
    useEffect(() => {
        if (procedureDetails) {
            setFormData(procedureDetails);
        }
    }, [procedureDetails]);

    // Fetch details and products when a procedure is selected
    useEffect(() => {
        if (selectedProcedure?.id && selectedProcedure.id !== selectedProcedureId) {
            setSelectedProcedureId(selectedProcedure.id);
            // Don't fetch details or reset edit mode for new items (id === 0)
            if (selectedProcedure.id !== 0) {
                getProcedureDetails(selectedProcedure.category, selectedProcedure.id);
                getProductsByProcedure(selectedProcedure.id);
                // Exit edit mode when switching procedures
                setEditMode(false);
            }
        }
    }, [selectedProcedure, selectedProcedureId, getProcedureDetails, getProductsByProcedure, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new procedure
    const handleAddNew = useCallback(() => {
        const newProcedure = {
            id: 0,
            category: config.category,
            title: '',
            description: '',
            comments: '',
            filePath: '',
            score: 0,
            lastUpdatedUtc: new Date().toISOString(),
        };
        setSelectedProcedure(newProcedure);
        setFormData(newProcedure);
        setSelectedProcedureId(0);
        setEditMode(true);
        setUpdateError(null);
    }, [config.category, setEditMode, setSelectedProcedure]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(procedureDetails);
        setEditMode(false);
        setUpdateError(null);
    }, [procedureDetails, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            setUpdateError(null);
            await updateProcedure(formData);
            setEditMode(false);
            // Refresh the list to show updated data
            onRefresh?.();
            // Also refresh the procedure details if viewing existing
            if (selectedProcedure?.id && selectedProcedure.id !== 0) {
                getProcedureDetails(selectedProcedure.category, selectedProcedure.id);
            }
        } catch (error: any) {
            setUpdateError(error.message || 'Failed to update procedure');
            console.error('Failed to save procedure:', error);
        }
    }, [formData, updateProcedure, setEditMode, onRefresh, selectedProcedure, getProcedureDetails]);

    // Handle field changes
    const handleFieldChange = useCallback((field: string, value: any) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    // Handle file upload
    const handleFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                await uploadFile({
                    category: 'Procedure',
                    fileName: file.name,
                    file: file,
                });

                // Update the filePath in form data after successful upload
                handleFieldChange('filePath', file.name);
            } catch (err) {
                console.error('Failed to upload file:', err);
            }

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, handleFieldChange]
    );

    // Trigger file input click
    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // Handle file download
    const handleDownload = useCallback(async () => {
        if (!formData?.filePath) return;
        try {
            await downloadFile({
                category: 'Procedure',
                fileName: formData.filePath,
            });
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    }, [formData?.filePath, downloadFile]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!procedures.length) return;

            const currentIndex = procedures.findIndex((p) => p.id === selectedProcedure?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < procedures.length - 1 ? currentIndex + 1 : 0;
                    setSelectedProcedure(procedures[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : procedures.length - 1;
                    setSelectedProcedure(procedures[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [procedures, selectedProcedure, setSelectedProcedure]);

    return (
        <TabContent>
            <ListDetailLayout>
                <TabListWrapper>
                    {loading && <LoadingSpinner text={`Loading ${config.title.toLowerCase()}...`} />}

                    {error && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading {config.title.toLowerCase()}: {error}
                        </div>
                    )}

                    {!loading && !error && procedures.length === 0 && (
                        <div className="empty-state">
                            <i className={`fas ${config.icon}`}></i>
                            <p>No {config.title.toLowerCase()} found</p>
                        </div>
                    )}

                    {!loading && procedures.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        {config.columns.map((column) => (
                                            <th key={column.key}>{column.header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {procedures.map((procedure) => (
                                        <tr
                                            key={procedure.id}
                                            className={`table-row ${selectedProcedure?.id === procedure.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedProcedure(procedure)}
                                        >
                                            {config.columns.map((column) => (
                                                <td key={column.key} className={`${column.key}-cell`}>
                                                    {column.render ? column.render(procedure) : procedure[column.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabListWrapper>

                <TabContentWrapper>
                    <DetailPanel>
                        {selectedProcedure ? (
                            <>
                                {isInEditMode && (
                                    <EditActionsBar>
                                        <button onClick={handleAddNew} className="btn-add-new" title="Add new procedure">
                                            <i className="fas fa-plus"></i> New Procedure
                                        </button>
                                        <div>
                                            <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                                <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                            </button>
                                            <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                                <i className="fas fa-times"></i> Cancel
                                            </button>
                                        </div>
                                    </EditActionsBar>
                                )}
                                <div className="detail-content">
                                    <div className="detail-header">
                                        <h3>{selectedProcedure.title}</h3>

                                        <div>
                                            <div className="header-actions">
                                                {canEdit && !isInEditMode && (
                                                    <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {procedureDetails?.lastUpdatedUtc && (
                                            <div className="last-updated">Last updated: {formatDateTime(procedureDetails.lastUpdatedUtc)}</div>
                                        )}
                                    </div>
                                    {updateError && (
                                        <div className="error-alert">
                                            <i className="fas fa-exclamation-triangle"></i> {updateError}
                                        </div>
                                    )}

                                    {detailsLoading ? (
                                        <LoadingSpinner text="Loading procedure details..." size="small" />
                                    ) : detailsError ? (
                                        <div className="error-state">
                                            <i className="fas fa-exclamation-triangle"></i> Error loading details: {detailsError}
                                        </div>
                                    ) : procedureDetails && formData ? (
                                        <div className="detail-body">
                                            <div className="detail-section">
                                                <div className="detail-grid">
                                                    <div className="detail-item">
                                                        <label>Title</label>
                                                        {isInEditMode ? (
                                                            <EditableInput
                                                                value={formData.title || ''}
                                                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                                                placeholder="Enter title"
                                                            />
                                                        ) : (
                                                            <span>{formData.title}</span>
                                                        )}
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>{config.isEmergency ? 'Type' : 'Category'}</label>
                                                        <span>{getProcedureCategoryName(formData.category)}</span>
                                                    </div>

                                                    {selectedProcedure.category === 1 && (
                                                        <div className="detail-item">
                                                            <label>Cleaning Difficulty</label>
                                                            {isInEditMode ? (
                                                                <select
                                                                    value={formData.score || 0}
                                                                    onChange={(e) => handleFieldChange('score', parseInt(e.target.value))}
                                                                    className="edit-select"
                                                                    title="Cleaning Difficulty"
                                                                >
                                                                    <option value="0">Unknown</option>
                                                                    <option value="1">Easy</option>
                                                                    <option value="2">Medium</option>
                                                                    <option value="3">High</option>
                                                                </select>
                                                            ) : (
                                                                <span>
                                                                    {formData.score === 0 && 'Unknown'}
                                                                    {formData.score === 1 && 'Easy'}
                                                                    {formData.score === 2 && 'Medium'}
                                                                    {formData.score === 3 && 'High'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="detail-grid">
                                                    <div className="detail-item">
                                                        <label>File</label>
                                                        {isInEditMode ? (
                                                            <>
                                                                <FileUploadRow>
                                                                    <EditableInput
                                                                        value={formData.filePath || ''}
                                                                        onChange={(e) => handleFieldChange('filePath', e.target.value)}
                                                                        placeholder="Enter file path"
                                                                    />
                                                                    <HiddenFileInput
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        onChange={handleFileUpload}
                                                                        accept=".pdf,.doc,.docx,.txt"
                                                                    />
                                                                    <UploadButton
                                                                        type="button"
                                                                        onClick={handleUploadClick}
                                                                        disabled={isUploading}
                                                                        title="Upload New Procedure"
                                                                    >
                                                                        <i className="fas fa-upload"></i>
                                                                        {isUploading ? 'Uploading...' : 'Upload New Procedure'}
                                                                    </UploadButton>
                                                                </FileUploadRow>
                                                                {uploadSuccess && (
                                                                    <SuccessMessage>
                                                                        <i className="fas fa-check-circle"></i> File uploaded successfully
                                                                    </SuccessMessage>
                                                                )}
                                                                {uploadError && (
                                                                    <ErrorMessage>
                                                                        <i className="fas fa-exclamation-circle"></i> {uploadError}
                                                                    </ErrorMessage>
                                                                )}
                                                            </>
                                                        ) : formData.filePath ? (
                                                            <FileLink
                                                                href={getFileUrl('Procedure', formData.filePath)}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleDownload();
                                                                }}
                                                                title="Download procedure file"
                                                            >
                                                                <i className="fas fa-file-pdf"></i>
                                                                {formData.filePath}
                                                                {isDownloading && <span> (Downloading...)</span>}
                                                            </FileLink>
                                                        ) : (
                                                            <span>No file</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="detail-section">
                                                <h4>{config.isEmergency ? 'Emergency Description' : 'Description'}</h4>
                                                {isInEditMode ? (
                                                    <EditableTextarea
                                                        value={formData.description || ''}
                                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                                        placeholder="Enter description"
                                                        rows={10}
                                                    />
                                                ) : (
                                                    <p className="description-content">{formData.description}</p>
                                                )}
                                            </div>
                                            <div className="detail-section">
                                                <h4>Comments</h4>
                                                {isInEditMode ? (
                                                    <EditableTextarea
                                                        value={formData.comments || ''}
                                                        onChange={(e) => handleFieldChange('comments', e.target.value)}
                                                        placeholder="Enter comments"
                                                        rows={5}
                                                    />
                                                ) : (
                                                    <p>{formData.comments || 'No comments'}</p>
                                                )}
                                            </div>
                                            {config.isEmergency && (
                                                <div className="detail-section">
                                                    <h4>Response Protocol</h4>
                                                    <p>Follow standard emergency response procedures and contact emergency services if required.</p>
                                                </div>
                                            )}
                                            {config.detailSections?.map((section, index) => (
                                                <div key={index} className="detail-section">
                                                    <h4>{section.title}</h4>
                                                    {section.content(procedureDetails)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="detail-body">
                                            <div className="detail-section">
                                                <h4>Basic Information</h4>
                                                <div className="detail-grid">
                                                    <div className="detail-item">
                                                        <label>ID</label>
                                                        <span>{selectedProcedure.Id}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>Title</label>
                                                        <span>{selectedProcedure.Title}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>{config.isEmergency ? 'Type' : 'Category'}</label>
                                                        <span>{getProcedureCategoryName(selectedProcedure.Category)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="detail-section">
                                                <h4>{config.isEmergency ? 'Emergency Description' : 'Description'}</h4>
                                                <p className="description-content">
                                                    {selectedProcedure.Description || selectedProcedure.TruncatedDescription}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="detail-placeholder">
                                <i className={`fas ${config.icon}`}></i>
                                <h3>
                                    Select {config.title.toLowerCase().replace(/s$/, '')}{' '}
                                    {config.title.toLowerCase().endsWith('s') ? '' : 'procedure'}
                                </h3>
                                <p>
                                    Choose {config.title.toLowerCase().replace(/s$/, '')}{' '}
                                    {config.title.toLowerCase().endsWith('s') ? '' : 'procedure'} from the list to view its details
                                </p>
                            </div>
                        )}
                    </DetailPanel>

                    {/* Products using this procedure - separate section outside DetailPanel */}
                    {selectedProcedure && <ProductsList products={products} loading={productsLoading} error={productsError} />}
                </TabContentWrapper>
            </ListDetailLayout>
        </TabContent>
    );
};

export default ProcedureTab;
